import { useNavigate } from 'react-router-dom';
import { KeyRound, Laptop } from 'lucide-react';
import type { CatalogItem } from '../api';
import { AtivoStatusBadge } from './AtivoStatusBadge';
import {
  LICENSE_TYPE_LABELS, formatarMoeda, situacaoLicenca, type SituacaoLicenca,
} from '../utils/formatters';

interface Props {
  ativos: CatalogItem[];
  isLoading: boolean;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  /** Clique numa licença: abre o detalhe (seats e vencimento). */
  onOpenLicense: (licenseId: string) => void;
}

const SITUACAO_LICENCA: Record<SituacaoLicenca, { label: string; className: string }> = {
  vigente: { label: 'Vigente', className: 'bg-green-100 text-green-800' },
  vencendo: { label: 'Vence em 30 dias', className: 'bg-yellow-100 text-yellow-800' },
  vencida: { label: 'Vencida', className: 'bg-red-100 text-red-800' },
  perpetua: { label: 'Perpétua', className: 'bg-gray-100 text-gray-700' },
};

function TipoBadge({ software }: { software: boolean }) {
  return software ? (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-indigo-100 px-2 py-0.5 text-xs text-indigo-800">
      <KeyRound className="h-3 w-3" /> Software / Licença
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
      <Laptop className="h-3 w-3" /> Ativo físico
    </span>
  );
}

const brl = (v: string | null | undefined) =>
  v !== null && v !== undefined ? formatarMoeda(v) : '—';

/**
 * Grid "Listar Ativos": ativos (físicos e software) e licenças (issue #464).
 *
 * Licenças vêm de outra tabela (`licenses`): a linha mostra nome,
 * tipo, dono, situação pelo vencimento e custo total, e o clique abre
 * o detalhe com seats em vez da página do ativo.
 */
export function AtivoTable({
  ativos, isLoading, hasActiveFilters, onClearFilters, onOpenLicense,
}: Props) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-6">
        <div className="animate-pulse space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-10 rounded bg-gray-100" />
          ))}
        </div>
      </div>
    );
  }

  if (ativos.length === 0) {
    if (hasActiveFilters) {
      return (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-500">
          <p>Nenhum ativo corresponde aos filtros aplicados.</p>
          {onClearFilters && (
            <button
              type="button"
              onClick={onClearFilters}
              className="mt-3 text-sm text-blue-600 hover:underline"
            >
              Limpar filtros
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-500">
        Nenhum ativo cadastrado ainda. Use "Novo" para começar.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left font-medium text-gray-600">Código</th>
            <th className="px-4 py-2 text-left font-medium text-gray-600">Descrição</th>
            <th className="px-4 py-2 text-left font-medium text-gray-600">Tipo</th>
            <th className="px-4 py-2 text-left font-medium text-gray-600">Categoria</th>
            <th className="px-4 py-2 text-left font-medium text-gray-600">Responsável</th>
            <th className="px-4 py-2 text-left font-medium text-gray-600">Localização do Ativo</th>
            <th className="px-4 py-2 text-left font-medium text-gray-600">Situação</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600">Valor</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {ativos.map((item) => {
            if (item.kind === 'license') {
              const situacao = SITUACAO_LICENCA[situacaoLicenca(item.expiresAt)];
              return (
                <tr
                  key={`license-${item.id}`}
                  className="cursor-pointer hover:bg-blue-50"
                  onClick={() => onOpenLicense(item.id)}
                >
                  <td className="px-4 py-2 font-mono text-xs text-gray-400">—</td>
                  <td className="px-4 py-2 text-gray-900">
                    {item.name}
                    <span className="ml-2 text-xs text-gray-500">
                      {item.usedSeats}/{item.totalSeats} seats
                    </span>
                  </td>
                  <td className="px-4 py-2"><TipoBadge software /></td>
                  <td className="px-4 py-2 text-gray-600">{LICENSE_TYPE_LABELS[item.licenseType] ?? item.licenseType}</td>
                  <td className="px-4 py-2 text-gray-600">{item.ownerCustodian?.name ?? '—'}</td>
                  <td className="px-4 py-2 text-gray-600">—</td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${situacao.className}`}>
                      {situacao.label}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right text-gray-800">{brl(item.totalCost)}</td>
                </tr>
              );
            }
            return (
              <tr
                key={`asset-${item.id}`}
                className="cursor-pointer hover:bg-blue-50"
                onClick={() => navigate(`/ativos/${item.id}`)}
              >
                <td className="px-4 py-2 font-mono text-xs text-gray-800">
                  {item.displayCode ?? item.assetCode}
                </td>
                <td className="px-4 py-2 text-gray-900">{item.description}</td>
                <td className="px-4 py-2"><TipoBadge software={item.assetType === 'software'} /></td>
                <td className="px-4 py-2 text-gray-600">{item.category?.name ?? '—'}</td>
                <td className="px-4 py-2 text-gray-600">{item.currentCustodian?.name ?? '—'}</td>
                <td className="px-4 py-2 text-gray-600">{item.location?.name ?? '—'}</td>
                <td className="px-4 py-2"><AtivoStatusBadge status={item.status} /></td>
                <td className="px-4 py-2 text-right text-gray-800">{brl(item.purchaseValue)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
